import { useMapContext } from '@components/map/MapContext';
import { useMapLayerMouseEvent } from '@hooks/useMapLayerMouseEvent';
import { useSelectorWithMapId } from '@hooks/useSelectorWithMapId';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { closePopupByMapIdAndType, updatePopupByMapId } from '@redux/maps/maps.reducer';
import { selectPopupsByMapId } from '@redux/maps/maps.selectors';
import { featureTypeToInfoDataType } from '@utils/map/map.constants';
import { checkIfPopupIsFromFeature, isSamePopupType, isTargetOrPeriSensorFeature } from '@utils/popup.utils';
import { uniqBy } from 'lodash';
import { GeoJSONFeature, MapEventOf } from 'mapbox-gl';
import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import { useMap } from 'react-map-gl/mapbox';

import { FeatureTypeEnum, LayerNameEnum } from '@/types/map.types';
import { MenuItems } from '@/types/menu.types';
import { InfoData, PopupTypeEnum } from '@/types/popup.types';
import { Activation } from '@/types/utm/flightPlan.types';

export const useInfo = (): [RefObject<HTMLDivElement | null>, MenuItems | null, { x: number; y: number } | null] => {
  const { current: mapbox } = useMap();
  const { mapId, infoLayers, isMeasureActive } = useMapContext();

  const currentPopups = useSelectorWithMapId(selectPopupsByMapId);
  const updatePopup = useWithDispatch(updatePopupByMapId);
  const closePopupByType = useWithDispatch(closePopupByMapIdAndType);

  const infoPickerRef = useRef<HTMLDivElement | null>(null);
  const [infoPickerItems, setInfoPickerItems] = useState<MenuItems | null>(null);
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | null>(null);

  const handleSingleItemClick = (feature: GeoJSONFeature, controlPosition: { x: number; y: number }) => {
    if (feature.properties) {
      const featureType = feature.properties.featureType;

      const featureValue = JSON.parse(feature.properties.value);
      let flightPlanActivation;

      if (featureType === FeatureTypeEnum.FLIGHT_PLAN) {
        flightPlanActivation =
          featureValue?.activations.find((activation: Activation) => activation.id === featureValue?.activationId) ??
          null;
      }

      const dataTypeFromFeature = featureTypeToInfoDataType[featureType as FeatureTypeEnum] ?? null;
      if (!dataTypeFromFeature) {
        return;
      }

      const alreadyOpenedPopup = currentPopups.some(
        (p) => isSamePopupType(p, PopupTypeEnum.INFO, dataTypeFromFeature) && checkIfPopupIsFromFeature(p, feature),
      );
      if (alreadyOpenedPopup) {
        closePopupByType({ mapId, type: PopupTypeEnum.INFO, dataType: dataTypeFromFeature });
      } else {
        updatePopup({
          mapId,
          popup: {
            type: PopupTypeEnum.INFO,
            position: controlPosition,
            data: {
              type: dataTypeFromFeature,
              featureValue,
              targetId: featureValue.id ?? null,
              openingDate: new Date(),
              activation: flightPlanActivation,
            } as InfoData,
          },
        });
      }
    }
  };

  const handleMultipleItemsOnClick = (features: GeoJSONFeature[], controlPosition: { x: number; y: number }) => {
    setInfoPickerItems(
      features
        .filter((feature: GeoJSONFeature) => !!feature.properties && !!feature.properties.selectOptionLabel)
        .reduce((accumulator, feature) => {
          const featureLabel = feature.properties!.selectOptionLabel;

          return {
            ...accumulator,
            [featureLabel]: {
              label: featureLabel,
              shouldNotTranslate: true,
              onAction: () => {
                setInfoPickerItems(null);
                setClickPosition(null);
                handleSingleItemClick(feature, controlPosition);
              },
            },
          };
        }, {}),
    );
    setClickPosition(controlPosition);
  };

  const handleMouseClick = (event: MapEventOf<'click'>) => {
    if (mapbox && !isMeasureActive) {
      const clickFeatures = event.features?.filter((feature) => !!feature.properties) ?? [];

      if (clickFeatures.length === 0) {
        setInfoPickerItems(null);
        return;
      }
      const controlPosition = { x: event.originalEvent.clientX, y: event.originalEvent.clientY };

      const hasTargetOrPeriSensorFeature = clickFeatures.some((feature) => isTargetOrPeriSensorFeature(feature));
      const features = hasTargetOrPeriSensorFeature
        ? clickFeatures.filter((feature) => isTargetOrPeriSensorFeature(feature))
        : clickFeatures;
      const featuresFromDistinctItems = uniqBy(features, (feature) => feature.properties?.value);

      // Prevent click propagation, if a cluster is selected and no target or perimeter sensor, do not select the features underneath
      const clusterLayerFeatures = mapbox.queryRenderedFeatures(event.point, {
        layers: [
          LayerNameEnum.SENSOR_CLUSTERS,
          LayerNameEnum.SENSOR_CLUSTER_OVERLAY,
          LayerNameEnum.SENSOR_CLUSTER_LEAVES,
        ],
      });
      if (!hasTargetOrPeriSensorFeature && clusterLayerFeatures.length) {
        return;
      }

      featuresFromDistinctItems.length === 1
        ? handleSingleItemClick(features[0], controlPosition)
        : handleMultipleItemsOnClick(features, controlPosition);
    }
  };
  useMapLayerMouseEvent('click', handleMouseClick, infoLayers);

  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (infoPickerRef.current && !infoPickerRef.current.contains(event.target as Node)) {
      setInfoPickerItems(null);
      setClickPosition(null);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  return [infoPickerRef, infoPickerItems, clickPosition];
};
